import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Edit, ArrowLeft } from "lucide-react";
import { getUsers } from "../../utils/getUser";
import { base64ToFile, convertDateFormatInUtc } from "../../utils/util";
import { getUserByid } from "../../reducers/authentication.slice";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { AuthContext } from "../../context/authContext";
import useMediaQuery from "../../components/shared/useMediaQuery";

function stringToColor(string) {
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

function getInitials(firstName, lastName) {
  return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
}

const PERMISSIONS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "live_orders", label: "Live Orders" },
  { key: "completed_orders", label: "Completed Orders" },
  { key: "pending_rsc_quotes", label: "Pending RSC Quotes" },
  { key: "shipping_and_tracking", label: "Shipping & Tracking" },
  { key: "pre_quoted_cooks", label: "Sell Catalog" },
  { key: "in_store_flips", label: "In-Store Flips" },
  { key: "user_management", label: "User Management" },
  { key: "analytics", label: "Analytics" },
  { key: "terms_and_conditions", label: "Terms & Conditions" },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [user, setUser] = useState({});
  const [showLoader, setShowLoader] = useState(false);
  const [payoutType, setPayoutType] = useState("ACH");
  const [paymentDetails, setPaymentDetails] = useState({});
  const [openDialog, setOpenDialog] = useState(false);

  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  useEffect(() => {
    setShowLoader(true);
    setUser(getUsers());
    const userId = getUsers()?.id;
    dispatch(getUserByid({ userId })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const newUserData = {
        ...getUsers(),
        discord_id: res?.payload?.discord_id,
        discord_role: res?.payload?.discord_role,
        permission_details: res?.payload?.permission_details,
        address_details: res?.payload?.address_details,
        payment_details: res?.payload?.payment_details,
        first_name: res?.payload?.first_name,
        last_name: res?.payload?.last_name,
        profile_picture: res?.payload?.profile_picture,
        role: res?.payload?.role,
        ip_address: res?.payload?.ip_address,
        last_login: res?.payload?.last_login,
        date_of_joining: res?.payload?.date_of_joining,
        login_type: res?.payload?.login_type,
        is_discord_verified: res?.payload?.is_discord_verified,
      };
      setUser(newUserData);
      setPayoutType(res?.payload?.payment_details?.payment_method);
      setPaymentDetails(res?.payload?.payment_details);
      localStorage.setItem("user", JSON.stringify(newUserData));
      setShowLoader(false);
    });
  }, []);

  const formatAddress = (addressObj) => {
    if (!addressObj) return "";
    const parts = [
      addressObj.address,
      addressObj.building,
      [addressObj.city, addressObj.state, addressObj.zipcode].filter(Boolean).join(", "),
    ].filter(Boolean);
    return parts.join(" , ");
  };

  const profileImgSrc = user?.profile_picture
    ? URL.createObjectURL(base64ToFile(user.profile_picture, "file", "image/*"))
    : null;

  const discordRoleColor = user?.discord_role?.[0]?.replace(" ", "");

  const infoRows = [
    { label: "Full Name", value: user?.first_name || user?.last_name ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : null },
    { label: "Email", value: user?.email },
    { label: "IP Address", value: user?.ip_address },
    { label: "Status", value: user?.status, isStatus: true },
    { label: "Login Type", value: user?.login_type?.toLowerCase() },
    { label: "Discord Username", value: user?.discord_id },
    { label: "Address", value: formatAddress(user?.address_details) },
    {
      label: "Discord Role",
      value: user?.discord_role
        ? user.discord_role.length > 0
          ? user.discord_role[0]?.toUpperCase()
          : "No Role"
        : null,
      isRole: true,
    },
    {
      label: "Signup Date",
      value: user?.date_of_joining
        ? convertDateFormatInUtc(user.date_of_joining, "MMMM D, YYYY hh:mm:ss A")
        : null,
    },
    {
      label: "Last Login",
      value: user?.last_login
        ? convertDateFormatInUtc(user.last_login, "MMMM D, YYYY hh:mm:ss A")
        : null,
    },
  ];

  return (
    <>
      {/* Image preview dialog */}
      {profileImgSrc && (
        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogContent style={{ maxWidth: 480, background: "#111", padding: 16 }}>
            <DialogHeader>
              <DialogTitle>Profile Picture</DialogTitle>
              <DialogDescription>{user?.first_name} {user?.last_name}</DialogDescription>
            </DialogHeader>
            <img src={profileImgSrc} alt="Profile" style={{ width: "100%", borderRadius: 8, objectFit: "contain", maxHeight: 400 }} />
          </DialogContent>
        </Dialog>
      )}

      <div style={{ display: "flex", justifyContent: "center", padding: "24px 16px" }}>
        <div style={{ width: "100%", maxWidth: 500 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "#ffffff85",
              cursor: "pointer",
              marginBottom: 16,
              width: "fit-content",
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
            <span style={{ fontFamily: "inter", fontSize: 14 }}>Back</span>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ fontSize: "1.3rem", fontWeight: "bold", color: "#fff", margin: 0 }}>My Profile</h2>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => navigate("edit", { state: { user_detail: user, isPaymentVia: false } })}
            >
              <Edit size={20} />
            </Button>
          </div>

          <div style={{ background: "rgba(255,255,255,0.05)", padding: 16, borderRadius: 8 }}>
            {/* Profile pic row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingBottom: 12,
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <span style={{ fontSize: 16, color: "gray" }}>Profile Pic</span>
              {profileImgSrc ? (
                <button
                  onClick={() => setOpenDialog(true)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  <img
                    src={profileImgSrc}
                    alt={user?.first_name}
                    style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "2px solid #fff" }}
                  />
                </button>
              ) : (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: stringToColor((user?.first_name || "") + (user?.last_name || "")),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 15,
                    fontWeight: 600,
                    border: "2px solid #fff",
                  }}
                >
                  {getInitials(user?.first_name, user?.last_name)}
                </div>
              )}
            </div>

            {/* Info rows */}
            {infoRows.map((row, i) =>
              row.value ? (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "center",
                    justifyContent: "space-between",
                    padding: "10px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                    gap: isMobile ? 4 : 8,
                  }}
                >
                  <span style={{ fontSize: 16, color: "gray", flexShrink: 0 }}>{row.label}</span>
                  {row.isStatus ? (
                    <span
                      style={{
                        fontSize: 15,
                        color: row.value === "ACTIVE" ? "#4caf50" : "#f44336",
                        background: row.value === "ACTIVE" ? "rgba(76, 175, 79, 0.3)" : "rgba(244, 67, 54, 0.30)",
                        padding: "5px 10px",
                        borderRadius: 5,
                      }}
                    >
                      {row.value}
                    </span>
                  ) : (
                    <span style={{ fontSize: 15, color: "#fff", textAlign: isMobile ? "left" : "right", overflowWrap: "anywhere", wordBreak: "break-word" }}>{row.value}</span>
                  )}
                </div>
              ) : null
            )}

            {/* Permissions (non-members only) */}
            {user.role && user.role !== "MEMBER" && (
              <div style={{ marginTop: 16 }}>
                <h3 style={{ fontSize: "1.3rem", fontWeight: "bold", color: "#fff", marginBottom: 8 }}>Permissions</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {PERMISSIONS.map((perm) => (
                    <label
                      key={perm.key}
                      style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#c3c8cc", cursor: "default" }}
                    >
                      <input
                        type="checkbox"
                        disabled
                        checked={!!user?.permission_details?.[perm.key]}
                        style={{ accentColor: "#00ADB5", width: 14, height: 14 }}
                        readOnly
                      />
                      {perm.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
